"use client";

import { create } from "zustand";
import type { PaymentMethod, Token, Currency, Network } from "@sdk/shared/types/config.types";
import type { EnrichedAd } from "@shared/src/services/ads.service";
import { FilterParams } from "@shared/src/utils/helpers/url-state";
import { urlStateHelper } from "@shared/src/utils/helpers/url-state";

interface P2PStore {
  // Data states
  networks: Network[] | null;
  currencies: Currency[] | null;
  tokens: Token[] | null;
  paymentMethods: PaymentMethod[] | null;
  ads: EnrichedAd[];
  botAds: EnrichedAd[][];
  isInitialized: boolean;
  totalItems: number;

  // Filter and Pagination states
  page: number;
  limit: number;
  minAmount: string;
  minPrice: string;
  minQuantity: string;
  tokenSymbol: string;
  currency: string | null;
  selectedCrypto: string;
  selectedPaymentMethod: string;
  selectedNetworks: string[];
  selectedTimeLimits: string[];
  orderBy: string;
  orderDirection: "asc" | "desc" | "";
  tradeType: "buy" | "sell";

  // Actions
  initializeData: (data: {
    networks: Network[];
    currencies: Currency[];
    tokens: Token[];
    paymentMethods: PaymentMethod[];
    ads: EnrichedAd[];
    botAds: EnrichedAd[][];
    totalItems: number;
    initialFilters?: FilterParams;
  }) => void;
  setIsInitialized: (value: boolean) => void;
  setAds: (ads: EnrichedAd[], totalItems: number) => void;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  setMinAmount: (amount: string) => void;
  setMinPrice: (price: string) => void;
  setMinQuantity: (quantity: string) => void;
  setTokenSymbol: (symbol: string) => void;
  setCurrency: (currency: string | null) => void;
  setSelectedCrypto: (cryptoId: string) => void;
  setSelectedPaymentMethod: (methodId: string) => void;
  setSelectedNetworks: (networkIds: string[]) => void;
  setSelectedTimeLimits: (timeLimits: string[]) => void;
  setOrderBy: (orderBy: string, direction: "asc" | "desc" | "") => void;
  setTradeType: (type: "buy" | "sell") => void;
  resetAll: () => void;
  updateSortingForCurrencyAndTradeType: () => void;
  validateSortingState: () => void;
}

export const useP2PStore = create<P2PStore>((set, get) => ({
  // Initial data states
  networks: null,
  currencies: null,
  tokens: null,
  paymentMethods: null,
  ads: [],
  botAds: [],
  isInitialized: false,
  totalItems: 0,

  // Initial filter and pagination states
  page: 1,
  limit: 10,
  minAmount: "",
  minPrice: "",
  minQuantity: "",
  tokenSymbol: "all",
  currency: null,
  selectedCrypto: "all",
  selectedPaymentMethod: "all",
  selectedNetworks: [],
  selectedTimeLimits: [],
  orderBy: "",
  orderDirection: "",
  tradeType: "sell",

  // Actions
  initializeData: data => {
    const store = get();

    set(state => ({
      networks: data.networks,
      currencies: data.currencies,
      tokens: data.tokens,
      paymentMethods: data.paymentMethods,
      ads: data.ads,
      totalItems: data.totalItems,
      isInitialized: true,
      botAds: data.botAds,
    }));

    if (data.initialFilters) {
      store.setPage(data.initialFilters.page || 1);
      store.setLimit(data.initialFilters.limit || 10);
      store.setMinAmount(data.initialFilters.minAmount || "");
      store.setMinPrice(data.initialFilters.minPrice || "");
      store.setMinQuantity(data.initialFilters.minQuantity || "");
      store.setTokenSymbol(data.initialFilters.tokenSymbol || "all");
      store.setSelectedCrypto(data.initialFilters.token || "all");
      store.setSelectedPaymentMethod(data.initialFilters.paymentMethod || "all");
      store.setSelectedNetworks(data.initialFilters.network ? data.initialFilters.network.split(",") : []);
      store.setSelectedTimeLimits(data.initialFilters.timeLimit ? data.initialFilters.timeLimit.split(",") : []);

      // Set trade type first, then currency to ensure proper sorting
      store.setTradeType(data.initialFilters.type || "sell");

      // Only set currency from initialFilters if no currency is currently selected
      // This preserves user's manual currency selection
      const currentCurrency = get().currency;
      if (!currentCurrency) {
        store.setCurrency(data.initialFilters.currency || null);
      }

      // Only override sorting if explicitly provided and no currency is set
      if (!data.initialFilters.currency && (data.initialFilters.orderBy || data.initialFilters.orderDirection)) {
        store.setOrderBy(data.initialFilters.orderBy || "", data.initialFilters.orderDirection || "");
      }
    } else {
      store.setTokenSymbol("all");
      store.setSelectedCrypto("all");
      store.setSelectedPaymentMethod("all");
      // Ensure proper sorting state for default values
      store.updateSortingForCurrencyAndTradeType();
    }
  },
  setIsInitialized: value => set(() => ({ isInitialized: value })),
  setAds: (ads, totalItems) => set(() => ({ ads, totalItems })),
  setPage: page => {
    set(() => ({ page }));
    urlStateHelper.pushToUrl({ page, limit: get().limit });
  },
  setLimit: limit => {
    set(() => ({ limit }));
    urlStateHelper.pushToUrl({ limit, page: get().page });
  },
  setMinAmount: amount => {
    set(() => ({ minAmount: amount, page: 1 }));
    urlStateHelper.pushToUrl({ minAmount: amount || undefined, page: 1 });
  },
  setMinPrice: price => {
    set(() => ({ minPrice: price, page: 1 }));
    urlStateHelper.pushToUrl({ minPrice: price || undefined, page: 1 });
  },
  setMinQuantity: quantity => {
    set(() => ({ minQuantity: quantity, page: 1 }));
    urlStateHelper.pushToUrl({ minQuantity: quantity || undefined, page: 1 });
  },
  setTokenSymbol: symbol => {
    set(() => ({ tokenSymbol: symbol, page: 1 }));
    urlStateHelper.pushToUrl({
      tokenSymbol: symbol === "all" ? undefined : symbol,
      page: 1,
    });
  },
  setCurrency: currency => {
    set(state => ({ currency, page: 1 }));
    // Use the consolidated sorting logic to ensure consistency
    get().updateSortingForCurrencyAndTradeType();
    urlStateHelper.pushToUrl({ currency: currency || undefined, page: 1 });
  },
  setSelectedCrypto: cryptoId => {
    set(() => ({ selectedCrypto: cryptoId, page: 1 }));
    urlStateHelper.pushToUrl({
      token: cryptoId === "all" ? undefined : cryptoId,
      page: 1,
    });
  },
  setSelectedPaymentMethod: methodId => {
    set(() => ({ selectedPaymentMethod: methodId, page: 1 }));
    urlStateHelper.pushToUrl({
      paymentMethod: methodId === "all" ? undefined : methodId,
      page: 1,
    });
  },
  setSelectedNetworks: networkIds => {
    set(() => ({ selectedNetworks: networkIds, page: 1 }));
    urlStateHelper.pushToUrl({
      network: networkIds.length > 0 ? networkIds.join(",") : undefined,
      page: 1,
    });
  },
  setSelectedTimeLimits: timeLimits => {
    set(() => ({ selectedTimeLimits: timeLimits, page: 1 }));
    urlStateHelper.pushToUrl({
      timeLimit: timeLimits.length > 0 ? timeLimits.join(",") : undefined,
      page: 1,
    });
  },
  setOrderBy: (orderBy, direction) => {
    set(() => ({ orderBy, orderDirection: direction, page: 1 }));
    // Do not push orderBy/orderDirection to the URL
  },
  // Validate and fix sorting state if inconsistent
  validateSortingState: () => {
    const state = get();
    const shouldSortByPrice = !!state.currency;
    const isCurrentlySortingByPrice = state.orderBy === "price";

    if (shouldSortByPrice !== isCurrentlySortingByPrice) {
      get().updateSortingForCurrencyAndTradeType();
    }
  },
  setTradeType: type => {
    set(state => ({ tradeType: type, page: 1 }));
    // Use the consolidated sorting logic to ensure consistency
    get().updateSortingForCurrencyAndTradeType();
    urlStateHelper.pushToUrl({ type, page: 1 });
  },
  resetAll: () =>
    set(state => {
      urlStateHelper.pushToUrl({
        type: state.tradeType,
        page: 1,
        limit: state.limit,
        minAmount: undefined,
        minPrice: undefined,
        minQuantity: undefined,
        tokenSymbol: undefined,
        currency: undefined,
        token: undefined,
        paymentMethod: undefined,
        network: undefined,
        timeLimit: undefined,
        orderBy: undefined,
        orderDirection: undefined,
      });

      const newState = {
        page: 1,
        minAmount: "",
        minPrice: "",
        minQuantity: "",
        tokenSymbol: "all",
        currency: null,
        selectedCrypto: "all",
        selectedPaymentMethod: "all",
        selectedNetworks: [],
        selectedTimeLimits: [],
        orderBy: "createdAt",
        orderDirection: "desc" as const,
        tradeType: state.tradeType,
      };

      // Ensure sorting state is consistent after reset
      setTimeout(() => get().updateSortingForCurrencyAndTradeType(), 0);

      return newState;
    }),
  updateSortingForCurrencyAndTradeType: () => {
    set(state => {
      let orderBy = state.orderBy;
      let orderDirection = state.orderDirection;

      if (state.currency) {
        // When currency is selected, always sort by price
        orderBy = "price";
        orderDirection = state.tradeType === "buy" ? "asc" : "desc";
      } else {
        // When no currency is selected, sort by creation date
        orderBy = "createdAt";
        orderDirection = "desc";
      }

      return { orderBy, orderDirection };
    });
  },
}));
